(() => {
  if (window.ColuluV2) {
    window.ColuluV2.init(document);
    return;
  }

  const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  const scrollBehavior = () => reducedMotion() ? 'auto' : 'smooth';

  const money = (cents, currency = 'JPY', locale = 'ja-JP') => {
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value);
  };

  const parseVariants = (root) => {
    const node = root.querySelector('[data-product-variants]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('Colulu v2: variant JSON could not be parsed.', error);
      return [];
    }
  };

  const initGallery = (root) => {
    const buttons = [...root.querySelectorAll('[data-gallery-thumb]')];
    const panels = [...root.querySelectorAll('[data-gallery-panel]')];
    if (!buttons.length || !panels.length) return null;

    const activate = (index, focus = false) => {
      buttons.forEach((button, itemIndex) => {
        const selected = itemIndex === index;
        button.setAttribute('aria-selected', String(selected));
        button.tabIndex = selected ? 0 : -1;
        if (focus && selected) button.focus();
      });
      panels.forEach((panel, itemIndex) => {
        const selected = itemIndex === index;
        panel.hidden = !selected;
        if (!selected) {
          panel.querySelectorAll('video, audio').forEach((media) => media.pause());
        }
      });
    };

    const onMediaSelect = (event) => {
      if (String(event.detail?.productId) !== String(root.dataset.productId)) return;
      const index = buttons.findIndex((button) => String(button.dataset.mediaId) === String(event.detail?.mediaId));
      if (index >= 0) activate(index);
    };

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => activate(index));
      button.addEventListener('keydown', (event) => {
        let next = index;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % buttons.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + buttons.length) % buttons.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = buttons.length - 1;
        else return;
        event.preventDefault();
        activate(next, true);
      });
    });

    root.ownerDocument.addEventListener('colulu:gallery-select', onMediaSelect);
    activate(Math.max(0, buttons.findIndex((button) => button.getAttribute('aria-selected') === 'true')));
    return () => root.ownerDocument.removeEventListener('colulu:gallery-select', onMediaSelect);
  };

  const initProductForm = (root) => {
    const select = root.querySelector('[data-variant-select]');
    const variants = parseVariants(root);
    if (!select || !variants.length) return null;

    const purchaseRoot = select.closest('[data-colulu-purchase-root]');
    const prices = [...root.querySelectorAll('[data-product-price]')];
    const compares = [...root.querySelectorAll('[data-product-compare]')];
    const availability = purchaseRoot?.querySelector('[data-product-availability]') || root.querySelector('[data-product-availability]');
    const button = purchaseRoot?.querySelector('[data-product-submit]') || root.querySelector('[data-product-submit]');
    const acknowledgement = purchaseRoot?.querySelector('[data-order-acknowledgement]');
    const currency = root.dataset.currency || 'JPY';
    const locale = root.dataset.locale || 'ja-JP';
    const addLabel = purchaseRoot?.dataset.addLabel || root.dataset.addLabel || 'カートに追加する';
    const soldOutLabel = purchaseRoot?.dataset.soldOutLabel || root.dataset.soldOutLabel || '売り切れ';
    const availableLabel = purchaseRoot?.dataset.availableLabel || root.dataset.availableLabel || '在庫あり';

    const update = () => {
      const variant = variants.find((item) => String(item.id) === select.value);
      if (!variant) return;
      prices.forEach((price) => {
        price.textContent = variant.price_formatted || money(variant.price, currency, locale);
      });
      compares.forEach((compare) => {
        compare.textContent = variant.compare_at_price > variant.price
          ? (variant.compare_at_price_formatted || money(variant.compare_at_price, currency, locale))
          : '';
        compare.hidden = !compare.textContent;
      });
      if (availability) availability.textContent = variant.available ? availableLabel : soldOutLabel;
      if (button) {
        const buttonLabel = button.querySelector('[data-product-submit-label]') || button;
        const acknowledged = !acknowledgement || acknowledgement.value !== '';
        button.dataset.productAvailable = variant.available ? 'true' : 'false';
        button.disabled = !variant.available || !acknowledged;
        buttonLabel.textContent = variant.available ? addLabel : soldOutLabel;
      }

      root.ownerDocument.dispatchEvent(new CustomEvent('colulu:gallery-select', {
        detail: {
          productId: root.dataset.productId,
          mediaId: variant.featured_media_id || variant.featured_media?.id,
        },
      }));

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState(history.state, '', url);
      root.ownerDocument.dispatchEvent(new CustomEvent('colulu:variant-change', {
        detail: { productId: root.dataset.productId, variant },
      }));
    };

    select.addEventListener('change', update);
    acknowledgement?.addEventListener('change', update);
    update();
    return () => {
      select.removeEventListener('change', update);
      acknowledgement?.removeEventListener('change', update);
    };
  };

  const initStickyAtc = (root) => {
    const price = root.querySelector('[data-sticky-price]');
    const button = root.querySelector('[data-sticky-purchase-link]');
    const currency = root.dataset.currency || 'JPY';
    const locale = root.dataset.locale || 'ja-JP';
    if (!button) return null;
    const onVariantChange = (event) => {
      if (String(event.detail.productId) !== String(root.dataset.productId)) return;
      const variant = event.detail.variant;
      if (price) price.textContent = variant.price_formatted || money(variant.price, currency, locale);
    };
    root.ownerDocument.addEventListener('colulu:variant-change', onVariantChange);
    const buyPanel = root.ownerDocument.querySelector(
      `[data-section-type="product-buy-panel"][data-product-id="${root.dataset.productId}"]`,
    ) || root.ownerDocument.querySelector('[data-product-buy-panel], [data-pdp-purchase-module], #buy');
    const purchaseTarget = buyPanel?.querySelector('form[action*="/cart/add"]') || buyPanel;
    if (purchaseTarget) {
      // Forms have a named "id" variant field, which shadows form.id.
      const targetId = purchaseTarget.getAttribute('id') || `ColuluPurchase-${root.dataset.productId}`;
      purchaseTarget.setAttribute('id', targetId);
      purchaseTarget.style.scrollMarginTop = '160px';
      button.href = `#${targetId}`;
    }
    const onPurchaseClick = (event) => {
      if (!purchaseTarget) return;
      event.preventDefault();
      purchaseTarget.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
    };
    button.addEventListener('click', onPurchaseClick);
    const visibilityTarget = purchaseTarget;
    let visibilityFrame = 0;
    const updateVisibility = () => {
      visibilityFrame = 0;
      if (!visibilityTarget) return;
      const rect = visibilityTarget.getBoundingClientRect();
      root.dataset.hidden = String(rect.bottom > 160 && rect.top < window.innerHeight);
    };
    const scheduleVisibility = () => {
      if (!visibilityFrame) visibilityFrame = requestAnimationFrame(updateVisibility);
    };
    window.addEventListener('scroll', scheduleVisibility, { passive: true });
    window.addEventListener('resize', scheduleVisibility);
    const observer = visibilityTarget && 'IntersectionObserver' in window
      ? new IntersectionObserver(scheduleVisibility, { threshold: .15 })
      : null;
    if (visibilityTarget && observer) observer.observe(visibilityTarget);
    updateVisibility();
    return () => {
      root.ownerDocument.removeEventListener('colulu:variant-change', onVariantChange);
      button.removeEventListener('click', onPurchaseClick);
      window.removeEventListener('scroll', scheduleVisibility);
      window.removeEventListener('resize', scheduleVisibility);
      if (visibilityFrame) cancelAnimationFrame(visibilityFrame);
      observer?.disconnect();
    };
  };

  const initSlider = (root) => {
    const cleanups = [];
    root.querySelectorAll('[data-colulu-slider]').forEach((slider) => {
      const track = slider.querySelector('[data-slider-track]');
      const previous = slider.querySelector('[data-slider-prev]');
      const next = slider.querySelector('[data-slider-next]');
      if (!track) return;
      const getStep = () => {
        const firstItem = track.children[0];
        if (!firstItem) return Math.max(track.clientWidth * .82, 280);
        const styles = window.getComputedStyle(track);
        const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return firstItem.getBoundingClientRect().width + gap;
      };
      const update = () => {
        const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
        const hasOverflow = maxScroll > 2;
        const visibleRatio = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;
        const scrollRatio = hasOverflow ? track.scrollLeft / maxScroll : 0;
        const progress = hasOverflow ? visibleRatio + scrollRatio * (1 - visibleRatio) : 1;
        slider.classList.toggle('has-overflow', hasOverflow);
        slider.style.setProperty('--pdp-slider-progress', `${Math.min(Math.max(progress, 0), 1) * 100}%`);
        if (previous) previous.disabled = !hasOverflow || track.scrollLeft <= 2;
        if (next) next.disabled = !hasOverflow || track.scrollLeft >= maxScroll - 2;
      };
      const move = (direction) => track.scrollBy({
        left: direction * getStep(),
        behavior: scrollBehavior(),
      });
      const onPrevious = () => move(-1);
      const onNext = () => move(1);
      previous?.addEventListener('click', onPrevious);
      next?.addEventListener('click', onNext);
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      requestAnimationFrame(update);
      cleanups.push(() => {
        previous?.removeEventListener('click', onPrevious);
        next?.removeEventListener('click', onNext);
        track.removeEventListener('scroll', update);
        window.removeEventListener('resize', update);
      });
    });
    return () => cleanups.forEach((cleanup) => cleanup());
  };

  const initSceneCarousels = (scope = document) => {
    const carousels = scope.matches?.('[data-scene-carousel]')
      ? [scope]
      : [...(scope.querySelectorAll?.('[data-scene-carousel]') || [])];

    carousels.forEach((carousel) => {
      if (carousel.dataset.initialized === 'true') return;
      const track = carousel.querySelector('[data-scene-track]');
      const previous = carousel.querySelector('[data-scene-prev]');
      const next = carousel.querySelector('[data-scene-next]');
      if (!track || !previous || !next) return;
      carousel.dataset.initialized = 'true';

      const getStep = () => {
        const firstItem = track.children[0];
        if (!firstItem) return Math.max(track.clientWidth * .82, 280);
        const styles = window.getComputedStyle(track);
        const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return firstItem.getBoundingClientRect().width + gap;
      };
      const update = () => {
        const max = Math.max(0, track.scrollWidth - track.clientWidth);
        const progress = max ? Math.min(1, Math.max(0, track.scrollLeft / max)) : 0;
        carousel.style.setProperty('--scene-progress', progress.toFixed(4));
        previous.disabled = progress <= .002;
        next.disabled = progress >= .998 || max === 0;
      };
      const move = (direction) => track.scrollBy({
        left: direction * getStep(),
        behavior: scrollBehavior(),
      });

      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      requestAnimationFrame(update);
    });
  };

  const initTabs = (root) => {
    const tabsRoot = root.matches('[data-colulu-tabs]') ? root : root.querySelector('[data-colulu-tabs]');
    if (!tabsRoot) return null;
    // Product catalog sections own their filtering, sorting and history state.
    // Avoid attaching a second set of tab listeners from the shared runtime.
    if (tabsRoot.hasAttribute('data-colulu-catalog-sort')) return null;
    const tabs = [...tabsRoot.querySelectorAll('[role="tab"]')];
    const panels = [...tabsRoot.querySelectorAll('[role="tabpanel"]')];
    const tabList = tabsRoot.querySelector('[role="tablist"]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!tabs.length || !panels.length) return null;

    const revealTabHorizontally = (tab) => {
      if (!tabList || !tab) return;
      const targetLeft = tab.offsetLeft - ((tabList.clientWidth - tab.offsetWidth) / 2);
      tabList.scrollTo({
        left: Math.max(0, targetLeft),
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    };

    const activate = (index, options = {}) => {
      const safeIndex = Math.max(0, Math.min(index, tabs.length - 1));
      tabs.forEach((tab, itemIndex) => {
        const selected = itemIndex === safeIndex;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      panels.forEach((panel, itemIndex) => {
        panel.hidden = itemIndex !== safeIndex;
      });
      const active = tabs[safeIndex];
      if (options.focus) active.focus();
      if (options.history) history.pushState(null, '', `#${active.dataset.tabHash}`);
      if (options.scroll) revealTabHorizontally(active);
    };

    tabsRoot.coluluActivateTab = activate;

    const fromHash = (options = {}) => {
      const hash = decodeURIComponent(location.hash.replace('#', ''));
      const index = tabs.findIndex((tab) => tab.dataset.tabHash === hash);
      activate(
        index >= 0 ? index : Math.max(0, tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')),
        { scroll: options.scroll === true && Boolean(hash) },
      );
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(index, { history: true, scroll: true }));
      tab.addEventListener('keydown', (event) => {
        let next = index;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % tabs.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        else return;
        event.preventDefault();
        activate(next, { focus: true, history: true, scroll: true });
      });
    });
    const onHistoryChange = () => fromHash({ scroll: true });
    window.addEventListener('hashchange', onHistoryChange);
    window.addEventListener('popstate', onHistoryChange);
    fromHash();
    return () => {
      window.removeEventListener('hashchange', onHistoryChange);
      window.removeEventListener('popstate', onHistoryChange);
      delete tabsRoot.coluluActivateTab;
    };
  };

  const initSoundSamples = (root) => {
    const buttons = [...root.querySelectorAll('[data-sound-sample]')];
    let audio = null;
    let active = null;
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const source = button.dataset.audioSrc;
        if (!source) return;
        if (active === button && audio && !audio.paused) {
          audio.pause();
          button.setAttribute('aria-pressed', 'false');
          active = null;
          return;
        }
        audio?.pause();
        buttons.forEach((item) => item.setAttribute('aria-pressed', 'false'));
        audio = new Audio(source);
        active = button;
        button.setAttribute('aria-pressed', 'true');
        audio.addEventListener('ended', () => {
          button.setAttribute('aria-pressed', 'false');
          active = null;
        }, { once: true });
        audio.play().catch(() => button.setAttribute('aria-pressed', 'false'));
      });
    });
    return () => {
      audio?.pause();
      active = null;
    };
  };

  const initSwrisV2 = (root) => {
    if (!root.matches('[data-colulu-v2-product]')) return null;
    const mainImage = root.querySelector('[data-colulu-v2-main-image], [data-swris-v2-main-image]');
    const thumbs = [...root.querySelectorAll('[data-colulu-v2-thumb], [data-swris-v2-thumb]')];
    const scrollButton = root.querySelector('[data-scroll-to-colulu-v2-buy], [data-scroll-to-swris-buy]');
    const buyPanel = root.querySelector('[data-colulu-v2-buy], [data-swris-v2-buy]');

    const activate = (button) => {
      if (!mainImage || !button) return;
      mainImage.src = button.dataset.imageSrc || mainImage.src;
      mainImage.alt = button.dataset.imageAlt || mainImage.alt;
      thumbs.forEach((thumb) => {
        const active = thumb === button;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', String(active));
      });
    };

    const thumbHandlers = thumbs.map((button) => {
      const handler = () => activate(button);
      button.addEventListener('click', handler);
      return [button, handler];
    });
    const scrollHandler = () => buyPanel?.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
    scrollButton?.addEventListener('click', scrollHandler);

    return () => {
      thumbHandlers.forEach(([button, handler]) => button.removeEventListener('click', handler));
      scrollButton?.removeEventListener('click', scrollHandler);
    };
  };

  const initTopV2Hero = (root) => {
    if (!root.matches('[data-colulu-top-v2-hero]')) return null;
    const slides = [...root.querySelectorAll('[data-colulu-top-v2-slide]')];
    const dots = [...root.querySelectorAll('[data-colulu-top-v2-dot]')];
    const prev = root.querySelector('[data-colulu-top-v2-prev]');
    const next = root.querySelector('[data-colulu-top-v2-next]');
    if (!slides.length) return null;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const interval = Math.max(2000, Number(root.dataset.autoplayMs) || 3200);
    const initialSlide = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    const startsAt = Date.parse(root.dataset.heroStartsAt || '');
    const scheduledSlide = slides.findIndex((slide) => slide.classList.contains(`hero__slide--${root.dataset.heroStartSlide}`));
    let scheduleTimer = null;
    let scheduleApplied = false;
    let current = 0;
    let timer = null;

    const activate = (index) => {
      current = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === current;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === current;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };

    const stop = () => {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    };
    const start = () => {
      if (reduceMotion || timer || slides.length < 2) return;
      timer = window.setInterval(() => activate(current + 1), interval);
    };
    const previous = () => { stop(); activate(current - 1); start(); };
    const following = () => { stop(); activate(current + 1); start(); };

    prev?.addEventListener('click', previous);
    next?.addEventListener('click', following);
    const dotHandlers = dots.map((dot, index) => {
      const handler = () => { stop(); activate(index); start(); };
      dot.addEventListener('click', handler);
      return [dot, handler];
    });
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);

    // Explicit timezone in the section setting; apply once without locking navigation.
    const applySchedule = () => {
      window.clearTimeout(scheduleTimer);
      if (scheduleApplied || !Number.isFinite(startsAt) || scheduledSlide < 0) return;
      const remaining = startsAt - Date.now();
      if (remaining > 0) {
        scheduleTimer = window.setTimeout(applySchedule, Math.min(remaining, 2147483647));
        return;
      }
      scheduleApplied = true;
      const scheduledSource = slides[scheduledSlide]?.querySelector('[data-hero-after-srcset]');
      const scheduledImage = slides[scheduledSlide]?.querySelector('[data-hero-after-src]');
      if (scheduledSource?.dataset.heroAfterSrcset) scheduledSource.srcset = scheduledSource.dataset.heroAfterSrcset;
      if (scheduledImage?.dataset.heroAfterSrc) scheduledImage.src = scheduledImage.dataset.heroAfterSrc;
      if (scheduledImage?.dataset.heroAfterAlt) scheduledImage.alt = scheduledImage.dataset.heroAfterAlt;
      stop();
      activate(scheduledSlide);
      if (!document.hidden && !root.matches(':hover') && !root.contains(document.activeElement)) start();
    };
    const onVisible = () => { if (!document.hidden) applySchedule(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', applySchedule);

    activate(initialSlide);
    start();
    applySchedule();
    return () => {
      stop();
      window.clearTimeout(scheduleTimer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', applySchedule);
      prev?.removeEventListener('click', previous);
      next?.removeEventListener('click', following);
      dotHandlers.forEach(([dot, handler]) => dot.removeEventListener('click', handler));
      root.removeEventListener('mouseenter', stop);
      root.removeEventListener('mouseleave', start);
      root.removeEventListener('focusin', stop);
      root.removeEventListener('focusout', start);
    };
  };

  const initTopSliders = (root) => {
    const sliders = root.matches('[data-top-slider]')
      ? [root]
      : [...root.querySelectorAll('[data-top-slider]')];
    if (!sliders.length) return null;

    const cleanups = sliders.map((slider) => {
      const track = slider.querySelector('[data-top-slider-track]');
      if (!track) return null;
      const update = () => {
        const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
        const hasOverflow = maxScroll > 2;
        const visibleRatio = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;
        const scrollRatio = hasOverflow ? track.scrollLeft / maxScroll : 0;
        const progress = hasOverflow ? visibleRatio + scrollRatio * (1 - visibleRatio) : 1;
        slider.classList.toggle('has-overflow', hasOverflow);
        slider.style.setProperty('--top-slider-progress', `${Math.min(Math.max(progress, 0), 1) * 100}%`);
      };
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      requestAnimationFrame(update);
      return () => {
        track.removeEventListener('scroll', update);
        window.removeEventListener('resize', update);
      };
    }).filter(Boolean);

    return () => cleanups.forEach((cleanup) => cleanup());
  };

  const initV2Header = (scope = document) => {
    const headers = scope.matches?.('[data-colulu-v2-site-header]')
      ? [scope]
      : [...(scope.querySelectorAll?.('[data-colulu-v2-site-header]') || [])];

    headers.forEach((header) => {
      if (header.dataset.coluluHeaderMounted === 'true') return;
      header.dataset.coluluHeaderMounted = 'true';
      const button = header.querySelector('[data-colulu-v2-menu-button]');
      const nav = header.querySelector('[data-colulu-v2-nav]');
      if (!button || !nav) return;
      const setOpen = (open) => {
        button.setAttribute('aria-expanded', String(open));
        button.setAttribute('aria-label', open ? 'メニューを閉じる' : 'メニューを開く');
        header.classList.toggle('is-menu-open', open);
        document.documentElement.classList.toggle('is-colulu-menu-open', open);
        document.body.classList.toggle('is-colulu-menu-open', open);
      };
      button.addEventListener('click', () => {
        setOpen(button.getAttribute('aria-expanded') !== 'true');
      });
      nav.addEventListener('click', (event) => {
        if (event.target.closest('a')) setOpen(false);
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') setOpen(false);
      });
    });
  };

  const initProductNavBehavior = (scope = document) => {
    const productNav = scope.matches?.('.pdp-local-nav')
      ? scope
      : scope.querySelector?.('.pdp-local-nav');
    if (!productNav || productNav.dataset.scrollBehaviorMounted === 'true') return;
    productNav.dataset.scrollBehaviorMounted = 'true';
    productNav.classList.remove('is-scroll-hidden');
  };

  const initGalleryCurrentState = (scope = document) => {
    const selector = '.morby-figma-thumbs,.swris-thumbs,.lullme-v2-thumbs,.hugwarmer-thumbs,.drimug-thumbs,.pdp-gallery-thumbs,.colulu-swris-v2-thumbs';
    const containers = scope.matches?.(selector)
      ? [scope]
      : [...(scope.querySelectorAll?.(selector) || [])];
    const thumbnailSelector = ':scope > .morby-figma-thumb, :scope > .swris-gallery-thumb, :scope > .lullme-v2-thumb, :scope > .lullme-swris-thumb, :scope > .hugwarmer-thumb, :scope > .drimug-thumb, :scope > .pdp-gallery-thumb, :scope > .colulu-swris-v2-thumb';

    containers.forEach((container) => {
      const sync = () => {
        container.querySelectorAll(thumbnailSelector).forEach((thumbnail) => {
          const current = thumbnail.classList.contains('is-selected')
            || thumbnail.classList.contains('is-active')
            || thumbnail.getAttribute('aria-selected') === 'true'
            || thumbnail.getAttribute('aria-pressed') === 'true';
          if (current) thumbnail.setAttribute('aria-current', 'true');
          else thumbnail.removeAttribute('aria-current');
        });
      };
      sync();
      if (container.dataset.coluluGalleryCurrentMounted === 'true') return;
      container.dataset.coluluGalleryCurrentMounted = 'true';
      container.addEventListener('click', () => requestAnimationFrame(sync));
      new MutationObserver(sync).observe(container, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'aria-selected', 'aria-pressed'],
      });
    });
  };

  const mount = (root) => {
    if (!(root instanceof Element) || root.dataset.coluluMounted === 'true') return;
    root.dataset.coluluMounted = 'true';
    const cleanups = [];
    const galleryCleanup = initGallery(root);
    if (galleryCleanup) cleanups.push(galleryCleanup);
    const formCleanup = initProductForm(root);
    if (formCleanup) cleanups.push(formCleanup);
    const stickyCleanup = initStickyAtc(root);
    if (stickyCleanup) cleanups.push(stickyCleanup);
    const sliderCleanup = initSlider(root);
    if (sliderCleanup) cleanups.push(sliderCleanup);
    const tabsCleanup = initTabs(root);
    if (tabsCleanup) cleanups.push(tabsCleanup);
    const soundCleanup = initSoundSamples(root);
    if (soundCleanup) cleanups.push(soundCleanup);
    const swrisCleanup = initSwrisV2(root);
    if (swrisCleanup) cleanups.push(swrisCleanup);
    const topHeroCleanup = initTopV2Hero(root);
    if (topHeroCleanup) cleanups.push(topHeroCleanup);
    const topSlidersCleanup = initTopSliders(root);
    if (topSlidersCleanup) cleanups.push(topSlidersCleanup);
    root.coluluCleanup = () => cleanups.forEach((cleanup) => cleanup());
  };

  const init = (scope = document) => {
    initV2Header(scope);
    initProductNavBehavior(scope);
    initGalleryCurrentState(scope);
    initSceneCarousels(scope);
    if (scope.matches?.('[data-colulu-section]')) mount(scope);
    scope.querySelectorAll?.('[data-colulu-section]').forEach(mount);
  };

  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-colulu-section]')
      ? event.target
      : event.target.querySelector?.('[data-colulu-section]');
    root?.coluluCleanup?.();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const eventTarget = event.target instanceof Element ? event.target : null;
    const root = eventTarget?.closest('[data-colulu-section]')
      || eventTarget?.querySelector?.('[data-colulu-section]');
    if (!root) return;

    const blockId = String(event.detail?.blockId || '');
    const tabsRoot = root.matches('[data-colulu-tabs]') ? root : root.querySelector('[data-colulu-tabs]');
    const tabs = tabsRoot ? [...tabsRoot.querySelectorAll('[role="tab"][data-tab-block-id]')] : [];
    const tabIndex = tabs.findIndex((tab) => tab.dataset.tabBlockId === blockId);

    if (tabIndex >= 0 && typeof tabsRoot.coluluActivateTab === 'function') {
      tabsRoot.coluluActivateTab(tabIndex);
    }

    const panel = [...root.querySelectorAll('[role="tabpanel"][data-tab-block-id]')]
      .find((item) => item.dataset.tabBlockId === blockId);
    const selectedBlock = eventTarget?.closest('[data-shopify-editor-block]') || eventTarget;
    const scrollTarget = panel || selectedBlock || root;
    requestAnimationFrame(() => {
      scrollTarget.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
    });
  });

  window.ColuluV2 = { init };
  init(document);
})();
